import { ViewportScroller } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractDynamicComponent } from '@components/abstracts';
import { Developer, FormulaireContactB2b, FormValid } from '@components/formulaire-contact-b2b/models';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button';
import { ImageComponent } from '@shared/components/image';
import { SelectComponent } from '@shared/components/select';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { HandleHtmlLinksDirective } from '@shared/directives';
import { GenerateRangePipe } from '@shared/pipe';
import { ContactB2B, ContactB2bService } from '@shared/services/contact-b2b';
import { CurrentLanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { envConfiguredNetworkId, isEmailValid, normalizeAndHash, validatePhoneByCountry } from '@shared/utils';
import { CommonStore } from '@stores';
import { combineLatest, EMPTY, map, of, switchMap, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

function generateStickerRotations(...rotationDegrees: number[]): string[] {
  return rotationDegrees.map((degrees) => `rotate(${degrees}deg)`);
}

@Component({
  selector: 'app-formulaire-contact-b2b',
  templateUrl: './formulaire-contact-b2b.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TitleComponent,
    SvgComponent,
    GenerateRangePipe,
    SelectComponent,
    HandleHtmlLinksDirective,
    ButtonComponent,
    ImageComponent,
    TranslatePipe,
  ],
})
export class FormulaireContactB2bComponent extends AbstractDynamicComponent<FormulaireContactB2b> {
  readonly formStart = viewChild<ElementRef<HTMLDivElement>>('formStart');

  protected readonly stickerRotations = generateStickerRotations(-5, 5, 1, -3);
  protected readonly availableAreas = computed(() =>
    Array.from(new Set(this.data.formSection.developers.flatMap((dev) => dev.area)))
      .sort()
      .map((area) => ({ label: area, key: area }))
  );
  protected readonly interest = signal<string>(null!);
  protected readonly describeYourself = signal<[string, string, string]>(['', '', '']);
  protected readonly selectedArea = signal<string>(null!);
  protected readonly filteredDevelopers = computed(() => {
    const area = this.selectedArea();
    if (!area) {
      return this.data.formSection.developers.slice(0, 4);
    }
    return this.data.formSection.developers.filter((d) => d.area.includes(area));
  });
  protected readonly firstName = signal<string>('');
  protected readonly lastName = signal<string>('');
  protected readonly email = signal<string>('');
  protected readonly phone = signal<string>('');
  protected readonly project = signal<string>('');

  protected readonly isFormSubmitted = signal(false);
  protected readonly isSending = signal(false);
  protected readonly isFormValid: Signal<FormValid> = computed(() => {
    let result: FormValid = {
      status: 'OK',
    };
    const errors: FormValid['errors'] = {};

    if (this.interest() === null) {
      errors.interest = 'GLOBAL.FORMS.INTEREST_REQUIRED_ERROR';
    }

    if (!this.selectedArea()) {
      errors.selectedArea = 'GLOBAL.FORMS.AREA_REQUIRED_ERROR';
    }

    if (this.firstName().length < 3) {
      errors.firstName = 'GLOBAL.FORMS.FIRST_NAME_MIN_LENGTH_ERROR';
    }

    if (this.lastName().length < 3) {
      errors.lastName = 'GLOBAL.FORMS.LAST_NAME_MIN_LENGTH_ERROR';
    }

    if (this.email().length === 0) {
      errors.email = 'GLOBAL.FORMS.EMAIL_REQUIRED_ERROR';
    } else if (!isEmailValid(this.email())) {
      errors.email = 'GLOBAL.FORMS.EMAIL_INVALID_ERROR';
    }

    if (this.phone().length === 0) {
      errors.phone = 'GLOBAL.FORMS.PHONE_REQUIRED_ERROR';
    } else if (!validatePhoneByCountry(this.phone(), this.language.region)) {
      errors.phone = 'GLOBAL.FORMS.PHONE_FORMAT_ERROR';
    }

    if (this.project().length === 0) {
      errors.message = 'GLOBAL.FORMS.MESSAGE_REQUIRED_ERROR';
    }

    if (Object.keys(errors).length > 0) {
      result = {
        status: 'KO',
        errors: errors,
      };
    }

    return result;
  });
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly contactB2bService = inject(ContactB2bService);
  private readonly commonStore = inject(CommonStore);
  private readonly logs = inject(LogsService);
  private readonly language = inject(CurrentLanguageService).language;
  private readonly emailAndPhoneSha256: Signal<{ email: string; phone: string }> = toSignal(
    combineLatest([
      toObservable(this.email), // reacts to email changes
      toObservable(this.phone), // reacts to email changes
      toObservable(this.isFormValid), // reacts to form validity changes
    ]).pipe(
      switchMap(([email, phone, form]) => {
        if (form.status !== 'OK' || !email || !phone) {
          return of({ email: '', phone: '' });
        }
        return combineLatest([normalizeAndHash(email), normalizeAndHash(phone)]).pipe(
          map(([emailHash, phoneHash]) => ({
            email: emailHash,
            phone: phoneHash,
          })),
          catchError((err: unknown) => {
            this.logs.warn(
              'FormulaireContactB2bComponent',
              'Failed to hash email/phone for GTM enhanced conversions',
              err
            );
            return of({ email: '', phone: '' });
          })
        );
      })
    ),
    { initialValue: { email: '', phone: '' } }
  );
  private readonly router = inject(Router);

  onInputChange($event: Event): void {
    const target = $event.target as HTMLInputElement;
    const { id, value } = target;
    this.logs.debug('FormulaireContactB2bComponent', `onInputChange - ${id}`);

    switch (id) {
      case 'interest-0':
      case 'interest-1':
      case 'interest-2':
      case 'interest-3':
        this.interest.set(value);
        break;
      case 'description-0':
      case 'description-1':
      case 'description-2': {
        const index = parseInt(id.split('-')[1]!);
        const currentDescriptions = [...this.describeYourself()];
        currentDescriptions[index] = value;
        this.describeYourself.set(currentDescriptions as [string, string, string]);
        break;
      }
      case 'first-name':
        this.firstName.set(value);
        break;
      case 'last-name':
        this.lastName.set(value);
        break;
      case 'email':
        this.email.set(value);
        break;
      case 'phone':
        this.phone.set(value);
        break;
      case 'comments':
        this.project.set(value);
        break;
    }
  }

  onAreaSelected(area: string): void {
    this.logs.debug('FormulaireContactB2bComponent', `onAreaSelected - ${area}`);
    this.selectedArea.set(area);
  }

  onSubmit(buttonLabel: string): void {
    if (this.isSending()) return;
    this.logs.debug('FormulaireContactB2bComponent', 'onSubmit', this.isFormValid());
    this.isFormSubmitted.set(true);

    if (this.isFormValid().status === 'KO') {
      this.scrollToFormStart();
    } else {
      this.isSending.set(true);
      const type = this.commonStore.selectPath((state) => state.forms.requestTypeB2bKey)() ?? '';
      const toSaved: ContactB2B = {
        type,
        project: this.project(),
        fullname: this.lastName(),
        firstname: this.firstName(),
        city: '',
        reason: this.interest(),
        other: this.describeYourself(),
        department: this.selectedArea().split('-')[0] ?? '',
        recipients: this.extractEmails(this.filteredDevelopers()) ?? [],
        email: this.email(),
        phone: this.phone().replace(/\s/g, ''),
        network: envConfiguredNetworkId(this.language.region),
      };
      this.logs.trace('FormulaireContactB2bComponent', 'toSaved', {
        type: toSaved.type,
        reason: toSaved.reason,
        department: toSaved.department,
        recipientsCount: toSaved.recipients.length,
      });
      this.contactB2bService
        .create(toSaved, true)
        .pipe(
          take(1),
          tap(() => {
            this.logs.info('FormulaireContactB2bComponent', 'Contact B2B form submission completed successfully');
            const gtm = this.data.gtmConfigBtnForm;
            this.emitGtmEvent({
              event: gtm?.event,
              group: gtm?.group,
              description: gtm?.description,
              context: gtm?.context,
              button_text_value: buttonLabel,
              event_type: 'submit',
              user_data: {
                user_email: this.emailAndPhoneSha256().email,
                user_phone: this.emailAndPhoneSha256().phone,
              },
            });
            void this.router.navigate([this.data.formSection.submitButton.url]);
          }),
          catchError((e) => {
            this.logs.error('FormulaireContactB2bComponent', 'Failed to submit contact B2B form', e);
            this.isSending.set(false);
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  /**
   * Extracts and returns a list of valid email addresses from an array of developers.
   *
   * @param {Developer[]} developers - The array of developer objects, each containing an email property.
   * @return {string[]} A list of valid, non-empty email strings.
   *
   * @example
   * const developers = [
   *   { id: 1, name: 'Alice', email: 'alice@example.com' },
   *   { id: 2, name: 'Bob', email: null },
   *   { id: 3, name: 'Charlie', email: '' }
   * ];
   * const emails = extractEmails(developers);
   * console.log(emails); // Output: ['alice@example.com']
   */
  private extractEmails(developers: Developer[]): string[] {
    return developers
      .map((developer) => developer.email)
      .filter((email): email is string => typeof email === 'string' && email.length > 0);
  }

  private scrollToFormStart(): void {
    const elRef = this.formStart();
    if (!elRef) {
      return;
    }

    this.viewportScroller.setOffset([0, 80]);

    const element = elRef.nativeElement as HTMLElement;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - 80;

    this.viewportScroller.scrollToPosition([0, offsetPosition], {
      behavior: 'smooth',
    });
  }
}
